import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  calculateScores,
  calculateRiskMaturityAlignment,
  getEnhancedGapAnalysis
} from '../../../../../shared/utils/scoring'
import { generateRoadmap } from '../../../../../shared/utils/roadmapGenerator'
import { calculateTAPS } from '../../../../../shared/utils/threatScoring'
import { calculateLossScenarios } from '../../../../../shared/utils/impactCalibration'
import threatProfiles from '../../../../../shared/data/threatProfiles.json'
import { generateEnablementPlan, generateExecutiveNarrative, getTimeHorizonOptions, getServiceTierOptions } from '../../../../../shared/utils/enablementPlanGenerator'
import { calculateInsuranceAdequacy } from '../../../../../shared/utils/insuranceAdequacy'
import { getEngagementDriverContext } from '../../../../../shared/utils/engagementDriver'
import { supabase } from '../../../../../shared/lib/supabase'
// enablementAI imports removed — AI generation now happens at assessment completion only

function EnablementPlanTab({ client, defaultSubTab, singleView = false }) {
  const [subTab, setSubTab] = useState(defaultSubTab || 'plan')

  // Sync when defaultSubTab changes (navigating between Plan Overview and Configuration)
  useEffect(() => {
    if (defaultSubTab) setSubTab(defaultSubTab)
  }, [defaultSubTab])
  const [enablementConfig, setEnablementConfig] = useState(null)
  const [insuranceReview, setInsuranceReview] = useState(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [configSaving, setConfigSaving] = useState(false)
  const [aiContent, setAiContent] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [narrativeEdits, setNarrativeEdits] = useState(null) // Admin edits to narrative fields
  const [manuallyEdited, setManuallyEdited] = useState(false)
  const [narrativeDirty, setNarrativeDirty] = useState(false)
  // Get latest completed assessment
  const allAssessments = client.assessments || []
  const completedAssessments = allAssessments
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
  const latestAssessment = completedAssessments[0]

  // Fetch enablement config
  useEffect(() => {
    if (!client.id) return
    const fetchConfig = async () => {
      setConfigLoading(true)
      try {
        const { data } = await supabase
          .from('client_enablement_config')
          .select('*')
          .eq('client_id', client.id)
          .maybeSingle()
        if (data) setEnablementConfig(data)
      } catch {
        // Table may not exist yet
      }
      setConfigLoading(false)
    }
    fetchConfig()
  }, [client.id])

  // Fetch latest cyber Insurance Policy Review so its findings flow into
  // the Enablement Plan as ranked, costed insurance initiatives.
  useEffect(() => {
    if (!client.id) return
    let cancelled = false
    supabase
      .from('client_insurance_policy_reviews')
      .select('id, coverage_limit_aggregate, retention, sublimits, notable_exclusions, findings, reviewed_at')
      .eq('client_id', client.id)
      .order('reviewed_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (!cancelled) setInsuranceReview(data?.[0] || null)
      })
    return () => { cancelled = true }
  }, [client.id])

  // Compute scores and enablement plan from latest assessment
  const { plan, narrative, scores, roadmap } = useMemo(() => {
    const empty = { plan: null, narrative: null, scores: null, roadmap: null }
    if (!latestAssessment?.responses) return empty

    try {
      const responses = latestAssessment.responses
      if (!responses?.maturityAssessment || Object.keys(responses.maturityAssessment).length === 0) {
        return empty
      }
      // Always compute full scores from responses — the DB-cached scores
      // are a sparse summary missing fields the pipeline needs
      const computedScores = calculateScores(responses)
      if (!computedScores?.overall) return empty
      const industry = responses?.businessScoping?.['bs-0']?.value || client.industry || 'Other'

      const al = calculateRiskMaturityAlignment(computedScores.riskAppetite, computedScores.maturity)
      const ga = getEnhancedGapAnalysis(al, industry, responses?.businessScoping)
      const engagementContext = getEngagementDriverContext(responses?.businessScoping)

      // Compute TAPS prioritization (needs threat profile + impact data)
      const threatContext = responses?.notes?.threatContext || responses?.threatContext || null
      const rawImpactData = responses?.notes?.impactData || responses?.impactData || null
      // Overlay the latest cyber Insurance Policy Review onto impact data
      // so coverage / sublimits / exclusions feed adequacy + insurance
      // initiative synthesis.
      let impactData = rawImpactData
      if (rawImpactData && insuranceReview) {
        const reviewLimit = parseFloat(insuranceReview.coverage_limit_aggregate) || 0
        impactData = {
          ...rawImpactData,
          insurance_coverage_limit: reviewLimit > 0 ? reviewLimit : rawImpactData.insurance_coverage_limit,
          insuranceCoverageLimit: reviewLimit > 0 ? reviewLimit : rawImpactData.insuranceCoverageLimit,
          client_retention: insuranceReview.retention || rawImpactData.client_retention,
          deductible: insuranceReview.retention || rawImpactData.deductible,
          has_cyber_insurance: reviewLimit > 0 ? true : rawImpactData.has_cyber_insurance,
          insuranceSublimits: insuranceReview.sublimits || {},
          insuranceExclusions: insuranceReview.notable_exclusions || [],
          insuranceReviewFindings: insuranceReview.findings || null,
          insuranceReviewId: insuranceReview.id,
          insuranceReviewedAt: insuranceReview.reviewed_at
        }
      }
      const profileKey = threatContext?.profileKey || 'TP-DEFAULT'
      const selectedProfile = threatProfiles.profiles.find(p => p.verticalKey === profileKey)
        || threatProfiles.profiles.find(p => p.verticalKey === 'TP-DEFAULT')

      const tapsResults = calculateTAPS(
        ga,
        {
          profile_key: profileKey,
          threat_actor_weights: threatContext?.threatActorWeights || {},
          attack_vector_weights: threatContext?.attackVectorWeights || {},
          target_asset_weights: threatContext?.assetWeights || {}
        },
        impactData || {},
        selectedProfile,
        engagementContext
      )

      const roadmap = generateRoadmap(
        tapsResults,
        impactData || {},
        ga,
        responses?.technologyScoping || null,
        engagementContext,
        responses?.businessScoping || null
      )

      let lossScenariosList = []
      if (impactData && threatContext) {
        try {
          const result = calculateLossScenarios(
            impactData,
            computedScores.overall.score,
            threatContext.profileKey,
            responses?.businessScoping
          )
          lossScenariosList = result?.scenarios || []
        } catch {
          // TIDA incomplete
        }
      }

      // Compute insurance adequacy so the generator can synthesize
      // coverage uplift / gap remediation / retention rebalance initiatives.
      const insuranceAdequacy = impactData
        ? calculateInsuranceAdequacy(impactData, { scenarios: lossScenariosList }, {})
        : null

      const enablementPlan = generateEnablementPlan({
        roadmap,
        gapAnalysis: ga,
        lossScenarios: lossScenariosList,
        tapsResults: tapsResults,
        scores: computedScores,
        threatContext: threatContext || {},
        businessScopingResponses: responses?.businessScoping || {},
        insuranceAdequacy,
        config: enablementConfig || {}
      })

      const execNarrative = generateExecutiveNarrative(enablementPlan)

      return {
        plan: enablementPlan,
        narrative: execNarrative,
        scores: computedScores,
        roadmap
      }
    } catch (err) {
      console.error('Enablement plan computation error:', err)
      return empty
    }
  }, [latestAssessment, client.industry, enablementConfig, insuranceReview])

  // Save config to Supabase
  const handleSaveConfig = async (updates) => {
    setConfigSaving(true)
    try {
      const configData = {
        client_id: client.id,
        ...enablementConfig,
        ...updates
      }
      delete configData.id
      delete configData.created_at
      delete configData.updated_at

      const { data, error } = await supabase
        .from('client_enablement_config')
        .upsert(configData, { onConflict: 'client_id' })
        .select()
        .single()

      if (!error && data) {
        setEnablementConfig(data)
      }
    } catch (err) {
      console.error('Error saving enablement config:', err)
    }
    setConfigSaving(false)
  }

  // Load AI draft from config on mount
  useEffect(() => {
    if (!enablementConfig) return
    if (enablementConfig.ai_narrative_draft || enablementConfig.ai_initiatives_draft) {
      const initiativeMap = {}
      if (enablementConfig.ai_initiatives_draft) {
        enablementConfig.ai_initiatives_draft.forEach(i => {
          initiativeMap[i.id] = { recommendation: i.recommendation, coiExplanation: i.coiExplanation }
        })
      }
      setAiContent({
        narrative: enablementConfig.ai_narrative_draft,
        initiativeMap,
        isPublished: !!enablementConfig.ai_published_at
      })
      // Initialize editable narrative from draft
      if (enablementConfig.ai_narrative_draft) {
        setNarrativeEdits(enablementConfig.ai_narrative_draft)
      }
      if (enablementConfig.ai_manually_edited) {
        setManuallyEdited(true)
      }
    }
  }, [enablementConfig])

  // AI content is generated at assessment completion, not on tab load.
  // Drafts are restored from enablementConfig in the load effect above.

  // Handle admin edits to narrative fields
  const handleNarrativeEdit = (field, value) => {
    setNarrativeEdits(prev => ({ ...prev, [field]: value }))
    setNarrativeDirty(true)
    setManuallyEdited(true)
  }

  // Save admin-edited narrative as draft
  const handleSaveNarrative = async () => {
    if (!narrativeEdits) return
    try {
      setAiContent(prev => ({ ...prev, narrative: narrativeEdits }))
      setNarrativeDirty(false)

      await supabase
        .from('client_enablement_config')
        .update({
          ai_narrative_draft: narrativeEdits,
          ai_manually_edited: true,
        })
        .eq('client_id', client.id)
    } catch (err) {
      console.error('Save narrative failed:', err)
    }
  }

  // Publish current draft to portal
  const handlePublishToPortal = async () => {
    const narrativeToPublish = narrativeEdits || aiContent?.narrative
    if (!narrativeToPublish) return
    try {
      await supabase
        .from('client_enablement_config')
        .update({
          ai_narrative_published: narrativeToPublish,
          ai_initiatives_published: aiContent?.initiativeMap
            ? Object.entries(aiContent.initiativeMap).map(([id, data]) => ({ id, ...data }))
            : null,
          ai_published_at: new Date().toISOString(),
        })
        .eq('client_id', client.id)

      setAiContent(prev => ({ ...prev, isPublished: true }))
    } catch (err) {
      console.error('Publish to portal failed:', err)
    }
  }

  // Unpublish from portal
  const handleUnpublish = async () => {
    try {
      await supabase
        .from('client_enablement_config')
        .update({
          ai_narrative_published: null,
          ai_initiatives_published: null,
          ai_published_at: null,
        })
        .eq('client_id', client.id)

      setAiContent(prev => ({ ...prev, isPublished: false }))
    } catch (err) {
      console.error('Unpublish failed:', err)
    }
  }

  const SUB_TABS = [
    { id: 'plan', label: 'Plan Overview' },
    { id: 'config', label: 'Configuration' }
  ]

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation — hidden when rendered as single view */}
      {!singleView && <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {SUB_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              subTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>}

      {/* Plan Overview sub-tab */}
      {subTab === 'plan' && (
        <PlanOverview
          plan={plan}
          narrative={narrative}
          roadmap={roadmap}
          latestAssessment={latestAssessment}
          client={client}
          aiContent={aiContent}
          aiLoading={aiLoading}
          narrativeEdits={narrativeEdits}
          narrativeDirty={narrativeDirty}
          onNarrativeEdit={handleNarrativeEdit}
          onSaveNarrative={handleSaveNarrative}
          onPublish={handlePublishToPortal}
          onUnpublish={handleUnpublish}
        />
      )}

      {/* Configuration sub-tab */}
      {subTab === 'config' && (
        <ConfigurationSubTab
          config={enablementConfig}
          onSave={handleSaveConfig}
          saving={configSaving}
          loading={configLoading}
        />
      )}
    </div>
  )
}

// ─── Plan Overview ───────────────────────────────────────────────
function PlanOverview({ plan, narrative, roadmap, latestAssessment, aiContent, aiLoading, narrativeEdits, narrativeDirty, onNarrativeEdit, onSaveNarrative, onPublish, onUnpublish, client }) {
  const [planTab, setPlanTab] = useState('summary')
  if (!latestAssessment) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Assessment Data Yet</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Complete an assessment to generate the enablement plan. You can pre-configure the plan settings in the Configuration tab.
        </p>
      </div>
    )
  }

  if (!plan) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Enablement Plan Generating...</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Complete the TIDA assessment (Threat Context + Impact Calibration) to generate the full enablement plan with cost-of-inaction data.
        </p>
      </div>
    )
  }

  const planTabs = [
    { id: 'summary', label: 'Executive Summary' },
    { id: 'initiatives', label: 'Initiative Detail' },
  ]

  // Build executive paragraph
  const totalInitiatives = plan?.summary?.totalInitiatives || plan?.phases?.reduce((s, p) => s + p.initiatives.length, 0) || 0
  const quickWinCount = plan?.summary?.quadrantDistribution?.['quick-win'] || 0
  const totalInvestment = narrative?.investmentSummary?.totalInvestment || ''
  const roi = roadmap?.summary?.roi

  return (
    <div>
      {/* Executive paragraph */}
      <div className="mb-6">
        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
          Based on the completed assessment, <span className="font-medium text-gray-800 dark:text-gray-200">{totalInitiatives} security initiatives</span> have been
          identified across multiple phases, with <span className="font-medium text-gray-800 dark:text-gray-200">{quickWinCount} quick wins</span> prioritized for immediate impact.
          {totalInvestment && <> Total estimated investment is <span className="font-medium text-gray-800 dark:text-gray-200">{totalInvestment}</span></>}
          {roi?.threeYearROI ? <> with a projected <span className="font-medium text-green-600 dark:text-green-400">{roi.threeYearROI}% three-year ROI</span></> : null}
          {roi?.paybackMonths ? <> and <span className="font-medium text-gray-800 dark:text-gray-200">{roi.paybackMonths}-month payback</span></> : null}.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
        <nav className="flex gap-6">
          {planTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setPlanTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                planTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {planTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="pt-6" />

    {planTab === 'summary' && (
    <div className="space-y-6">
      {/* Executive Narrative */}
      {narrative && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Executive Summary</h3>
            <div className="flex items-center gap-2">
              {aiLoading && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-purple-600 dark:text-purple-400">
                  <div className="w-3 h-3 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
                  Generating...
                </span>
              )}
              {narrativeDirty && (
                <button
                  onClick={onSaveNarrative}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Save Changes
                </button>
              )}
              {aiContent?.narrative && !narrativeDirty && (
                aiContent.isPublished ? (
                  <button
                    onClick={onUnpublish}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Published to Portal
                  </button>
                ) : (
                  <button
                    onClick={onPublish}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Publish to Portal
                  </button>
                )
              )}
            </div>
          </div>

          {/* Editable headline */}
          {narrativeEdits ? (
            <textarea
              value={narrativeEdits.headline || ''}
              onChange={e => onNarrativeEdit('headline', e.target.value)}
              className="w-full text-base font-medium text-gray-800 dark:text-gray-200 bg-transparent border-0 border-b border-dashed border-gray-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-0 p-0 pb-1 mb-4 resize-none"
              rows={1}
            />
          ) : (
            <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-4">{narrative.headline}</p>
          )}

          {/* Metric cards (not editable) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Investment</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">{narrative.investmentSummary.totalInvestment}</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Annual Risk Exposure</p>
              <p className="text-lg font-bold text-red-600 dark:text-red-400">{narrative.investmentSummary.totalExposureReduction}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Net Value</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{narrative.investmentSummary.netValue}</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Timeline</p>
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{narrative.investmentSummary.timeHorizon}</p>
            </div>
          </div>

          {/* Editable key findings */}
          <div className="space-y-2 mb-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Key Findings</p>
            {narrativeEdits?.keyFindings ? (
              narrativeEdits.keyFindings.map((finding, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-3 flex-shrink-0" />
                  <textarea
                    value={finding}
                    onChange={e => {
                      const updated = [...narrativeEdits.keyFindings]
                      updated[idx] = e.target.value
                      onNarrativeEdit('keyFindings', updated)
                    }}
                    className="flex-1 text-sm text-gray-700 dark:text-gray-300 bg-transparent border-0 border-b border-dashed border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-0 p-0 pb-1 resize-none"
                    rows={1}
                  />
                </div>
              ))
            ) : (
              (narrative.keyFindings || []).map((finding, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-700 dark:text-gray-300">{finding}</p>
                </div>
              ))
            )}
          </div>

          {/* Editable recommendation */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Recommendation</p>
            {narrativeEdits ? (
              <textarea
                value={narrativeEdits.recommendation || ''}
                onChange={e => onNarrativeEdit('recommendation', e.target.value)}
                className="w-full text-sm text-blue-800 dark:text-blue-200 bg-transparent border-0 focus:ring-0 p-0 resize-none"
                rows={3}
              />
            ) : (
              <p className="text-sm text-blue-800 dark:text-blue-200">{narrative.recommendation}</p>
            )}
          </div>

          {/* Editable investment rationale */}
          {(narrativeEdits?.investmentRationale || aiContent?.narrative?.investmentRationale) && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Investment Rationale</p>
              {narrativeEdits ? (
                <textarea
                  value={narrativeEdits.investmentRationale || ''}
                  onChange={e => onNarrativeEdit('investmentRationale', e.target.value)}
                  className="w-full text-sm text-gray-600 dark:text-gray-400 bg-transparent border-0 border-b border-dashed border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-0 p-0 pb-1 resize-none"
                  rows={2}
                />
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">{aiContent.narrative.investmentRationale}</p>
              )}
            </div>
          )}

          {/* ROI Summary — nested inside Executive Summary card */}
          {roadmap?.summary?.roi && (
            <div className="mt-5 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-medium text-green-800 dark:text-green-200 mb-3">
                Return on Investment Estimate
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total Investment</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    ${roadmap.summary.roi.totalInvestment?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Est. Annual Savings</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    ${roadmap.summary.roi.estimatedAnnualSavings?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Payback Period</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {roadmap.summary.roi.paybackMonths ? `${roadmap.summary.roi.paybackMonths} months` : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">3-Year ROI</div>
                  <div className="text-lg font-bold text-primary-600 dark:text-accent-300">
                    {roadmap.summary.roi.threeYearROI}%
                  </div>
                </div>
              </div>
              {roadmap.summary.roi.methodology && (
                <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 text-center">
                  {roadmap.summary.roi.methodology}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quadrant Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Initiative Classification</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'quick-win', label: 'Quick Wins', colorClass: 'green' },
            { key: 'strategic-investment', label: 'Strategic', colorClass: 'blue' },
            { key: 'efficiency-play', label: 'Efficiency', colorClass: 'amber' },
            { key: 'defer-accept', label: 'Defer', colorClass: 'gray' }
          ].map(q => (
            <div key={q.key} className={`p-4 rounded-lg border ${
              q.colorClass === 'green' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
              q.colorClass === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
              q.colorClass === 'amber' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' :
              'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{q.label}</span>
              <p className={`text-2xl font-bold mt-1 ${
                q.colorClass === 'green' ? 'text-green-600 dark:text-green-400' :
                q.colorClass === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                q.colorClass === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                'text-gray-600 dark:text-gray-400'
              }`}>
                {plan.summary.quadrantDistribution[q.key] || 0}
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
    )}

    {planTab === 'initiatives' && (
    <div className="space-y-6">
      {/* Phase-by-Phase Initiative Cards */}
      {plan.phases.map(phase => (
        <div key={phase.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white capitalize">
                  {phase.id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{phase.timeline}</p>
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {phase.initiatives.length} initiative{phase.initiatives.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {phase.initiatives.map(init => (
              <div key={init.id} className="px-6 py-4 space-y-2">
                {/* Row 1: Name + badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{init.name}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    init.quadrant.quadrant === 'quick-win' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                    init.quadrant.quadrant === 'strategic-investment' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                    init.quadrant.quadrant === 'efficiency-play' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                    'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {init.quadrant.label}
                  </span>
                  {init.tierRelevance === 'primary' && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                      Tier Focus
                    </span>
                  )}
                  {init.categoryId === 'cyber-insurance' && (
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 inline-flex items-center gap-1"
                      title="Insurance-related initiative — coordinate with broker around renewal cycle"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Insurance
                    </span>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                    {init.categoryId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>

                {/* Row 2: AI recommendation (if available) */}
                {aiContent?.initiativeMap?.[init.id]?.recommendation && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {aiContent.initiativeMap[init.id].recommendation}
                  </p>
                )}

                {/* Row 3: Metrics grid */}
                <div className="grid grid-cols-3 gap-4 pt-1">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Implementation</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{init.implementationCost.formatted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Cost of Inaction</p>
                    <p className="text-sm font-bold text-red-600 dark:text-red-400">
                      ${init.costOfInaction.annualizedCost?.toLocaleString() || '-'}/yr
                    </p>
                    {aiContent?.initiativeMap?.[init.id]?.coiExplanation && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {aiContent.initiativeMap[init.id].coiExplanation}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Net Value</p>
                    <p className={`text-sm font-bold ${init.netValue >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {init.netValueFormatted}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Confidence Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Data Confidence</h3>
        <div className="flex gap-4">
          {Object.entries(plan.summary.confidenceDistribution).map(([level, count]) => (
            <div key={level} className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${
                level === 'high' ? 'bg-green-500' : level === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
              }`} />
              <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{level}: {count}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          High = Ponemon-validated control effectiveness. Medium = Loss scenario attribution. Low = Sector baseline estimate.
        </p>
      </div>
    </div>
    )}

    </div>
  )
}

// ─── Assessment Results Sub-Tab ──────────────────────────────────
function AssessmentResultsSubTab({ latestAssessment, scores, benchmarkData, gapAnalysis, completedAssessments = [] }) {
  if (!latestAssessment) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Completed Assessment</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Complete an assessment to see results here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Link to full results page */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {latestAssessment.title || 'Latest Assessment'}
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Completed {new Date(latestAssessment.completedAt).toLocaleDateString()} · Overall Score: {scores?.overall?.score || '-'}%
            </p>
          </div>
          <Link
            to={`/clients/${client.id}?section=program-overview`}
            className="btn-primary text-sm"
          >
            View Full Results
          </Link>
        </div>
      </div>

      {/* Quick summary cards */}
      {scores && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Maturity Score</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{scores.overall.score}%</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{scores.overall.maturityLevel?.label}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Benchmark Gap</p>
            <p className={`text-2xl font-bold ${benchmarkData?.gap >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {benchmarkData?.gap > 0 ? '+' : ''}{benchmarkData?.gap || 0}%
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{benchmarkData?.severity?.label}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Risk Appetite</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{scores.riskAppetite?.averageAppetite || '-'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{scores.riskAppetite?.averageLabel}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Categories Assessed</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{scores.overall.categoryCount || '-'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Security domains</p>
          </div>
        </div>
      )}

      {/* Weakest / Strongest categories */}
      {scores?.overall?.weakestCategories && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Weakest Categories</h4>
            <div className="space-y-3">
              {scores.overall.weakestCategories.slice(0, 5).map(cat => (
                <div key={cat.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{cat.name}</span>
                  <span className={`text-sm font-bold ${cat.score < 40 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {cat.score}%
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Strongest Categories</h4>
            <div className="space-y-3">
              {(scores.overall.strongestCategories || []).slice(0, 5).map(cat => (
                <div key={cat.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{cat.name}</span>
                  <span className="text-sm font-bold text-green-600 dark:text-green-400">{cat.score}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gap analysis summary */}
      {gapAnalysis && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Gap Analysis Summary</h4>
          <div className="flex gap-4 flex-wrap">
            {gapAnalysis.summary.criticalCount > 0 && (
              <span className="px-3 py-1 text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                {gapAnalysis.summary.criticalCount} Critical Gap{gapAnalysis.summary.criticalCount !== 1 ? 's' : ''}
              </span>
            )}
            {gapAnalysis.summary.highCount > 0 && (
              <span className="px-3 py-1 text-sm font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full">
                {gapAnalysis.summary.highCount} Significant Gap{gapAnalysis.summary.highCount !== 1 ? 's' : ''}
              </span>
            )}
            {gapAnalysis.summary.moderateCount > 0 && (
              <span className="px-3 py-1 text-sm font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                {gapAnalysis.summary.moderateCount} Moderate
              </span>
            )}
            {gapAnalysis.summary.alignedCount > 0 && (
              <span className="px-3 py-1 text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                {gapAnalysis.summary.alignedCount} Aligned
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Overall urgency: {gapAnalysis.summary.overallUrgency} · Estimated remediation: {gapAnalysis.summary.estimatedTotalCost?.formatted}
          </p>
        </div>
      )}

      {/* All assessments list */}
      {completedAssessments.length > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Previous Assessments</h4>
          <div className="space-y-2">
            {completedAssessments.slice(1).map(a => (
              <Link
                key={a.id}
                to={`/clients/${client.id}?section=program-overview`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">{a.title || 'Assessment'}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(a.completedAt).toLocaleDateString()} · {a.scores?.overall?.score || '-'}%
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Configuration Sub-Tab ───────────────────────────────────────
function ConfigurationSubTab({ config, onSave, saving, loading }) {
  const timeHorizonOptions = getTimeHorizonOptions()
  const serviceTierOptions = getServiceTierOptions()

  const [localConfig, setLocalConfig] = useState({
    time_horizon: config?.time_horizon || 'standard',
    service_tier: config?.service_tier || 'strategic-partner',
    show_cost_of_inaction: config?.show_cost_of_inaction !== false
  })

  // Sync when config loads
  useEffect(() => {
    if (config) {
      setLocalConfig({
        time_horizon: config.time_horizon || 'standard',
        service_tier: config.service_tier || 'strategic-partner',
        show_cost_of_inaction: config.show_cost_of_inaction !== false
      })
    }
  }, [config])

  const handleSave = () => {
    onSave(localConfig)
  }

  const hasChanges = config
    ? (localConfig.time_horizon !== (config.time_horizon || 'standard') ||
       localConfig.service_tier !== (config.service_tier || 'strategic-partner') ||
       localConfig.show_cost_of_inaction !== (config.show_cost_of_inaction !== false))
    : true

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Time Horizon */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/30 pb-2 mb-2">Time Horizon</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">How quickly should the plan be executed?</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {timeHorizonOptions.map(opt => (
            <button
              key={opt.id}
              onClick={() => setLocalConfig(prev => ({ ...prev, time_horizon: opt.id }))}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                localConfig.time_horizon === opt.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{opt.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Service Tier */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/30 pb-2 mb-2">Service Tier</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">What level of advisory engagement does this client have?</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {serviceTierOptions.map(opt => (
            <button
              key={opt.id}
              onClick={() => setLocalConfig(prev => ({ ...prev, service_tier: opt.id }))}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                localConfig.service_tier === opt.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{opt.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.focus}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{opt.advisorInvolvement}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Client Portal Visibility */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/30 pb-2 mb-2">Client Portal Visibility</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Control what the client sees in their portal plan view.</p>
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            role="switch"
            aria-checked={localConfig.show_cost_of_inaction}
            onClick={() => setLocalConfig(prev => ({ ...prev, show_cost_of_inaction: !prev.show_cost_of_inaction }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localConfig.show_cost_of_inaction ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              localConfig.show_cost_of_inaction ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Show Cost of Inaction</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Display financial exposure data on the client's portal plan page</p>
          </div>
        </label>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
            hasChanges && !saving
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  )
}

export default EnablementPlanTab
